import type {
	StoryblokRichTextDoc,
	StoryblokRichTextInput,
	StoryblokRichTextNode,
	StoryblokRichTextRendererMap,
} from '@storyblok/richtext';
// Callers go through renderStoryblokRichText so they always get the default
// resolvers, and eslint.config.js enforces it. The wrapper lives here, so this
// is the one file that gets to import the raw function.
// eslint-disable-next-line no-restricted-imports
import { renderRichText } from '@storyblok/richtext';

import {
	createImageRenderer,
	createInternalLinkRenderer,
} from './custom-resolvers';

/**
 * The one place we turn Storyblok richtext into HTML.
 *
 * Storyblok hands us internal links as a plain slug, without the leading slash
 * — "en/deals" instead of "/en/deals". Browsers cope, thanks to the
 * <base href="/"> in index.html, but anything that ignores that tag reads the
 * link as relative to the page it sits on. So a link to "en/y" on /en/x gets
 * fetched as /en/en/y, and that 404s. createInternalLinkRenderer puts the slash
 * back, and doing it here means nobody has to remember to.
 *
 * Pass renderers to override one of the defaults for a single call.
 */
export function renderStoryblokRichText(
	content: StoryblokRichTextInput,
	renderers: StoryblokRichTextRendererMap = {},
): string {
	return renderRichText(content, {
		renderers: {
			image: createImageRenderer(),
			link: createInternalLinkRenderer(),
			...renderers,
		},
	});
}

/**
 * Storyblok richtext (or an already rendered string) as plain text, for places
 * that take text only — an aria-label reads "<b>" out literally.
 *
 * A document is walked rather than rendered and stripped: its text nodes hold
 * the words unescaped, so nothing has to be decoded again.
 */
export function renderStoryblokRichTextAsText(
	content: StoryblokRichTextDoc | string | undefined,
): string {
	if (!content) {
		return '';
	}

	if (typeof content === 'string') {
		return decodeEntities(stripTags(content));
	}

	return content.content.map(textOf).join(' ').trim();
}

function textOf(node: StoryblokRichTextNode): string {
	if ('text' in node) {
		return node.text;
	}

	return 'content' in node && node.content
		? node.content.map(textOf).join('')
		: '';
}

function stripTags(html: string): string {
	let text = '';
	let insideTag = false;

	for (const character of html) {
		if (character === '<') {
			insideTag = true;
		} else if (character === '>') {
			insideTag = false;
		} else if (!insideTag) {
			text += character;
		}
	}

	return text;
}

function decodeEntities(text: string): string {
	return text
		.replaceAll(/&#(\d+);/g, (_, code: string) =>
			String.fromCodePoint(Number(code)),
		)
		.replaceAll('&nbsp;', ' ')
		.replaceAll('&lt;', '<')
		.replaceAll('&gt;', '>')
		.replaceAll('&quot;', '"')
		.replaceAll('&amp;', '&');
}
